import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, AlertTriangle, CheckCircle2, Edit3, Save,
  Eye, Cpu, ScanLine, Layers, X,
} from 'lucide-react';


const KIND_AR = {
  supplier_invoice: 'فاتورَة مورِد',
  customer_invoice: 'فاتورَة عَميل',
  receipt_voucher:  'سَنَد قَبض',
  payment_voucher:  'سَنَد صَرف',
  bank_statement:   'كَشف بَنكي',
  contract:         'عَقد',
  purchase_order:   'أَمر شِراء',
  delivery_note:    'سَنَد تَسليم',
  credit_note:      'إشعار دائِن',
  debit_note:       'إشعار مَدين',
  expense_receipt:  'فاتورَة مَصروف',
  attachment:       'مُرفَق',
  other:            'أُخرى',
};

const LAYER_AR = {
  filename: 'اسم الملَف', mime: 'نَوع MIME', pdf_meta: 'بَيانات PDF',
  keywords: 'كَلِمات مِفتاحِيَّة', llm: 'الذَّكاء الاصطِناعي',
  default: 'افتِراضي',
};

const FIELD_AR = {
  vat_number:     'الرَّقم الضَّريبي',
  invoice_number: 'رَقم الفاتورَة',
  reference_no:   'المَرجِع',
  iban:           'IBAN',
  sadad_number:   'رَقم سَداد',
  document_date:  'تاريخ المُستَنَد',
  currency:       'العُملَة',
  supplier_name:  'اسم المورِد',
  customer_name:  'اسم العَميل',
  subtotal:       'الإجمالي قَبل الضَّريبَة',
  vat_amount:     'مَبلَغ الضَّريبَة',
  total:          'الإجمالي',
  due_date:       'تاريخ الاستِحقاق',
};

const SOURCE_BADGE = {
  regex:  { ar: 'استِخراج آلي', cls: 'bg-slate-200 text-slate-700' },
  llm:    { ar: 'ذَكاء اصطِناعي', cls: 'bg-violet-100 text-violet-700' },
  manual: { ar: 'يَدَوي',         cls: 'bg-emerald-100 text-emerald-700' },
};

const confidenceTone = (c) => {
  if (c == null) return 'text-slate-400';
  if (c >= 0.85)  return 'text-emerald-700 bg-emerald-50';
  if (c >= 0.65)  return 'text-amber-700 bg-amber-50';
  return 'text-rose-700 bg-rose-50';
};


export default function DocExtractionPanel({ doc, onMutated }) {
  const [extraction, setExtraction] = useState(doc?.extraction || null);
  const [processing, setProcessing] = useState(false);
  const [allowLlm, setAllowLlm]    = useState(true);
  const [edits, setEdits]          = useState({});  // { field: stringValue }
  const [savingEdits, setSavingEdits] = useState(false);

  useEffect(() => {
    setExtraction(doc?.extraction || null);
    setEdits({});
  }, [doc?.id, doc?.extraction]);

  const runProcess = async () => {
    setProcessing(true);
    try {
      const { data } = await axiosInstance.post(
        `/document-hub/${doc.id}/process`,
        { allow_llm: allowLlm, auto_classify: true },
      );
      setExtraction(data.extraction);
      setEdits({});
      toast.success('تَمَّت المُعالَجَة');
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشِلَت المُعالَجَة');
    } finally { setProcessing(false); }
  };

  const onFieldChange = (k, v) => setEdits((p) => ({ ...p, [k]: v }));

  const saveCorrections = async () => {
    const overrides = {};
    for (const [k, v] of Object.entries(edits)) {
      if (v == null || v === '') continue;
      let val = v;
      // numeric coercion for known numeric fields
      if (['subtotal', 'vat_amount', 'total', 'amount'].includes(k)) {
        const n = parseFloat(v);
        if (!Number.isNaN(n)) val = n;
      }
      overrides[k] = { value: val };
    }
    if (!Object.keys(overrides).length) {
      toast.message('لا تَعديلات لِلحِفظ');
      return;
    }
    setSavingEdits(true);
    try {
      const { data } = await axiosInstance.post(
        `/document-hub/${doc.id}/extraction/correct`,
        { overrides, reason: 'تَصحيح يَدَوي' },
      );
      setExtraction(data.extraction);
      setEdits({});
      toast.success('تَمَّ حِفظ التَّصحيحات');
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الحِفظ');
    } finally { setSavingEdits(false); }
  };

  const fields = extraction?.fields || {};
  const fieldKeys = useMemo(() => {
    const baseOrder = Object.keys(FIELD_AR);
    const present   = new Set(Object.keys(fields));
    const ordered   = baseOrder.filter((k) => present.has(k));
    const extras    = [...present].filter((k) => !baseOrder.includes(k));
    return [...ordered, ...extras];
  }, [fields]);

  const td = extraction?.type_detection;
  const ocr = extraction?.ocr;
  const val = extraction?.validation;

  return (
    <div className="space-y-3" data-testid="extraction-panel">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-2 pb-2 border-b">
        <div className="flex items-center gap-1 text-[10px] uppercase font-semibold text-slate-500">
          <Sparkles className="w-3 h-3" /> الاستِخراج الذَّكي (F2)
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] inline-flex items-center gap-1 cursor-pointer select-none">
            <input
              type="checkbox" checked={allowLlm}
              onChange={(e) => setAllowLlm(e.target.checked)}
              data-testid="extract-allow-llm"
              className="w-3 h-3 accent-violet-600"
            />
            <span>استِخدام LLM</span>
          </label>
          <button
            onClick={runProcess} disabled={processing}
            data-testid="extract-run-btn"
            className="text-xs font-semibold rounded bg-indigo-600 hover:bg-indigo-700
                       text-white px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {processing
              ? <><Loader2 className="w-3 h-3 animate-spin" /> جارٍ المُعالَجَة…</>
              : <><ScanLine className="w-3 h-3" /> {extraction ? 'إعادَة' : 'تَشغيل'}</>}
          </button>
        </div>
      </div>

      {!extraction ? (
        <div
          className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 text-center"
          data-testid="extract-empty"
        >
          لَم يَتِم تَشغيل المُعالَجَة بَعد. اضغَط زِرّ التَّشغيل أَعلاه لِبَدء استِخراج النَّوع وَالحُقول.
        </div>
      ) : (
        <>
          {/* Type detection card */}
          <div className="rounded-lg border bg-white p-2 space-y-1" data-testid="extract-type-card">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-slate-500 inline-flex items-center gap-1">
                <Layers className="w-3 h-3" /> اكتِشاف النَّوع
              </span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${confidenceTone(td?.confidence)}`}>
                {(td?.confidence * 100 || 0).toFixed(0)}%
              </span>
            </div>
            <div className="text-xs font-semibold" data-testid="extract-type-kind">
              {KIND_AR[td?.kind] || td?.kind || '—'}
            </div>
            <div className="text-[10px] text-slate-500">
              عَبر: <span className="font-mono">{LAYER_AR[td?.layer] || td?.layer}</span>
            </div>
            {td?.candidates?.length > 1 && (
              <details className="text-[10px] text-slate-600 pt-1">
                <summary className="cursor-pointer">المُرَشَّحون ({td.candidates.length})</summary>
                <ul className="mt-1 space-y-0.5">
                  {td.candidates.map((c, i) => (
                    <li key={i} className="font-mono">
                      {LAYER_AR[c.layer] || c.layer}: {KIND_AR[c.kind] || c.kind} · {(c.confidence * 100).toFixed(0)}%
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* OCR card */}
          <div className="rounded-lg border bg-white p-2" data-testid="extract-ocr-card">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-slate-500 inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> OCR
              </span>
              <span className="text-[10px] font-mono text-slate-500">
                {ocr?.method} · {ocr?.text_length || 0} حَرف
              </span>
            </div>
            {ocr?.text_preview ? (
              <pre className="text-[10px] font-mono whitespace-pre-wrap bg-slate-50
                              rounded p-1.5 max-h-28 overflow-y-auto leading-snug">
                {ocr.text_preview}
              </pre>
            ) : (
              <div className="text-[10px] text-slate-400">لا يُوجَد نَصّ.</div>
            )}
          </div>

          {/* Fields */}
          <div className="rounded-lg border bg-white p-2" data-testid="extract-fields-card">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-slate-500 inline-flex items-center gap-1">
                <Cpu className="w-3 h-3" /> الحُقول المُستَخرَجَة
              </span>
              {Object.keys(edits).length > 0 && (
                <button
                  onClick={saveCorrections} disabled={savingEdits}
                  data-testid="extract-save-corrections"
                  className="text-[11px] font-semibold rounded bg-emerald-600 hover:bg-emerald-700
                             text-white px-2 py-0.5 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {savingEdits
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Save className="w-3 h-3" />}
                  حِفظ ({Object.keys(edits).length})
                </button>
              )}
            </div>
            {fieldKeys.length === 0 ? (
              <div className="text-[10px] text-slate-400 py-2 text-center">
                لَم تُستَخرَج حُقول.
              </div>
            ) : (
              <div className="space-y-1.5">
                {fieldKeys.map((k) => {
                  const f = fields[k] || {};
                  const src = SOURCE_BADGE[f.source] || SOURCE_BADGE.regex;
                  const editing = k in edits;
                  return (
                    <div key={k} className="border-b last:border-b-0 pb-1.5" data-testid={`extract-field-${k}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[11px] font-semibold text-slate-700">
                          {FIELD_AR[k] || k}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className={`text-[9px] rounded px-1 py-0.5 ${src.cls}`}>{src.ar}</span>
                          <span className={`text-[9px] font-mono rounded px-1 py-0.5 ${confidenceTone(f.confidence)}`}>
                            {((f.confidence ?? 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <input
                          type="text"
                          value={editing ? edits[k] : (f.value ?? '')}
                          onChange={(e) => onFieldChange(k, e.target.value)}
                          data-testid={`extract-input-${k}`}
                          className="flex-1 rounded border border-slate-300 px-1.5 py-1 text-[11px] font-mono"
                        />
                        {editing && (
                          <button
                            onClick={() => setEdits((p) => {
                              const n = { ...p }; delete n[k]; return n;
                            })}
                            data-testid={`extract-revert-${k}`}
                            className="p-1 hover:bg-slate-100 rounded text-slate-500"
                            title="تَراجُع"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Validation findings */}
          <div className="rounded-lg border bg-white p-2" data-testid="extract-validation-card">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-slate-500 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> التَّحَقُّق
              </span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${confidenceTone(val?.overall_confidence)}`}>
                إجمالي: {((val?.overall_confidence ?? 0) * 100).toFixed(0)}%
              </span>
            </div>
            {!val?.findings?.length ? (
              <div className="text-[10px] text-emerald-700 py-1.5 inline-flex items-center gap-1"
                   data-testid="extract-validation-clean">
                <CheckCircle2 className="w-3 h-3" /> لا ملاحَظات.
              </div>
            ) : (
              <ul className="space-y-1">
                {val.findings.map((f, i) => (
                  <li
                    key={i}
                    className={`text-[10px] rounded px-1.5 py-1 inline-flex items-start gap-1
                                ${f.severity === 'critical' ? 'bg-rose-50 text-rose-700'
                                  : f.severity === 'warning' ? 'bg-amber-50 text-amber-700'
                                  : 'bg-sky-50 text-sky-700'}`}
                    data-testid={`extract-finding-${f.field}`}
                  >
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{FIELD_AR[f.field] || f.field}:</span>{' '}
                      {f.message}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer meta */}
          <div className="text-[10px] text-slate-400 font-mono text-center">
            engine={extraction.engine_version} · {extraction.elapsed_ms} ms
          </div>
        </>
      )}
    </div>
  );
}
